import { useEffect, useRef, useState } from "react";
import { projectIdFromLocation, projectUrl } from "../model/projectRoute.ts";
import type { StoredProject } from "../model/storage.ts";

/**
 * Keeps the address bar's "/p/<id>" in sync with `loaded`, and the reverse:
 * restores whatever project a bookmarked/shared URL names at mount, and
 * again on browser Back/Forward. The ONLY place that calls
 * history.pushState/replaceState, so every other state transition is picked
 * up automatically just by `loaded` changing.
 */
export function useProjectUrlSync(
  loaded: StoredProject | null,
  openSavedProject: (id: string) => Promise<void>,
  closeProject: () => void
) {
  // A "/p/<id>" URL at mount starts the app straight into restoring that
  // project instead of flashing Landing.tsx first — the actual async
  // restore happens in the mount effect below.
  const [restoringFromUrl, setRestoringFromUrl] = useState(() => projectIdFromLocation() !== null);

  // Restores the project named in the URL, if any — the async half of the
  // `restoringFromUrl` lazy initializer above. Mount-only: a LATER URL
  // change (browser Back/Forward) is handled by the popstate listener
  // further down instead.
  useEffect(() => {
    const id = projectIdFromLocation();
    if (id === null) return;
    void openSavedProject(id).finally(() => setRestoringFromUrl(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflects `loaded`'s id into the URL.
  const hasSyncedUrlRef = useRef(false);
  useEffect(() => {
    // Still resolving the initial "/p/<id>" restore — without this guard, a
    // bookmarked/shared project URL would flash to "/" and back the moment
    // the async restore above finishes.
    if (restoringFromUrl) return;
    const activeId = loaded?.id ?? null;
    const urlId = projectIdFromLocation();
    if (activeId === urlId) {
      hasSyncedUrlRef.current = true;
      return;
    }
    const url = projectUrl(activeId);
    if (!hasSyncedUrlRef.current) {
      // First real settle since mount — never worth its own "Back" entry.
      window.history.replaceState(null, "", url);
    } else {
      window.history.pushState(null, "", url);
    }
    hasSyncedUrlRef.current = true;
  }, [loaded?.id, restoringFromUrl]);

  // Browser Back/Forward: the address bar already changed by the time this
  // fires, so this only has to make `loaded` catch up to it.
  useEffect(() => {
    function onPopState() {
      const urlId = projectIdFromLocation();
      const currentId = loaded?.id ?? null;
      if (urlId === currentId) return;
      closeProject(); // no-op if nothing is loaded
      if (urlId !== null) void openSavedProject(urlId);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  return { restoringFromUrl };
}
